/**
 * UPI-app launcher definitions and href builder.
 *
 * Extracted from ManualUpiPanel. Given a UPI app id (gpay/phonepe/paytm)
 * and the current OS, `buildLauncherHref` returns the correct URI to
 * open that app's home / send-to-UPI screen.
 *
 * ANDROID CHROME 90+ INTENT URI QUIRK
 * -----------------------------------
 * Modern Chrome on Android silently blocks bare custom schemes like
 * `phonepe://` when fired from an `<a href>` — even with a real user
 * gesture — to mitigate deep-link spam. The Android-blessed escape hatch
 * is the `intent://` URI, which Chrome, Samsung Internet, Brave and Edge
 * all honour:
 *
 *   intent://[host/path]#Intent;
 *     scheme=<custom-scheme>;
 *     package=<android-package>;          ← pins target so Chrome doesn't show the chooser
 *     S.browser_fallback_url=<https-url>; ← if app not installed, go here
 *   end
 *
 * iOS Safari has no equivalent — but its bare-scheme handling is reliable
 * (registered URL types in the app's Info.plist), so we just emit
 * `phonepe://` etc. directly there.
 *
 * WHY THIS IS STILL "MANUAL MODE"
 * -------------------------------
 * The URIs below carry NO `upi://pay` payload, NO amount, NO tn, NO tr.
 * They open the app's home / UPI screen, period. The customer still
 * pastes the VPA and types the amount themselves — exactly the no-
 * merchant-intent flow that dodges PSP risk-engine declines.
 */

export const UPI_APP_DEFS = {
    gpay: {
        label: "Google Pay",
        iosScheme: "tez://upi/",
        androidScheme: "tez",
        androidPkg: "com.google.android.apps.nbu.paisa.user",
        fallback: "https://pay.google.com",
        color: "border-blue-300 hover:bg-blue-50",
    },
    phonepe: {
        label: "PhonePe",
        iosScheme: "phonepe://",
        androidScheme: "phonepe",
        androidPkg: "com.phonepe.app",
        fallback: "https://www.phonepe.com",
        color: "border-purple-300 hover:bg-purple-50",
    },
    paytm: {
        label: "Paytm",
        iosScheme: "paytmmp://",
        androidScheme: "paytmmp",
        androidPkg: "net.one97.paytm",
        fallback: "https://paytm.com",
        color: "border-cyan-300 hover:bg-cyan-50",
    },
};

/** Canonical order the launcher row is rendered in. */
export const UPI_APP_ORDER = ["gpay", "phonepe", "paytm"];

/**
 * Build the correct launcher URI for the given app + OS. Returns the
 * fallback marketing URL on desktop / unknown UA so the click never
 * silently no-ops.
 */
export function buildLauncherHref(def, { isIOS, isAndroid }) {
    if (isIOS) return def.iosScheme;
    if (isAndroid) {
        const fb = encodeURIComponent(def.fallback);
        return `intent://#Intent;scheme=${def.androidScheme};package=${def.androidPkg};S.browser_fallback_url=${fb};end`;
    }
    // Desktop or unknown UA → just send to the app's marketing site.
    // Customer is on a phone in 99.9% of cases anyway.
    return def.fallback;
}

/**
 * Convenience: build the full { id, label, color, href } list ready to
 * render as launcher buttons. Callers just `.map()` over the result.
 */
export function buildLaunchers(osInfo) {
    return UPI_APP_ORDER.map((id) => {
        const def = UPI_APP_DEFS[id];
        return { id, ...def, href: buildLauncherHref(def, osInfo) };
    });
}
